import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { FooterLink } from '../shared/components/footer/footer';
import { AuthService, LoginResponse, LoginUser } from '../core/services/auth.service';

type LoginStep = 'credentials' | 'otp';

interface NormalizedAuthResponse {
  status: boolean;
  message: string;
  token?: string;
  user?: LoginUser;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
  title = 'Flyash';
  currentYear = new Date().getFullYear();

  loginLinks: FooterLink[] = [
    { label: 'Privacy Policy', url: '#' },
    { label: 'Terms of Use', url: '#' },
    { label: 'Site Map', url: '#' },
    { label: 'Contact Us', url: '#' },
  ];

  showPassword = false;
  captchaCode = '';
  captchaError = false;
  activeTab: 'industry' | 'official' | 'public' = 'industry';
  loginForm: FormGroup;
  otpForm: FormGroup;

  loginStep: LoginStep = 'credentials';
  pendingUsername = '';

  showRegTypeModal = false;

  // API integration states
  isLoading = false;
  isResendLoading = false;
  loginError = '';
  loginSuccess = '';

  resendCountdown = 0;
  private resendTimerId: ReturnType<typeof setInterval> | null = null;

  readonly otpFields = ['otp1', 'otp2', 'otp3', 'otp4'] as const;

  private readonly RESEND_COOLDOWN_SECONDS = 60;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private authService: AuthService,
  ) {
    this.loginForm = this.fb.group({
      userId: ['', Validators.required],
      password: ['', Validators.required],
      rememberMe: [false],
      captchaInput: ['', Validators.required],
    });

    this.otpForm = this.createOtpForm();
    this.generateCaptcha();
  }

  ngOnInit(): void {
    document.body.classList.add('login-page');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('login-page');
    this.clearResendTimer();
  }

  private createOtpForm(): FormGroup {
    const controls: Record<string, unknown[]> = {};
    for (const field of this.otpFields) {
      controls[field] = ['', [Validators.required, Validators.pattern(/^\d$/)]];
    }
    return this.fb.group(controls);
  }

  get isOtpStep(): boolean {
    return this.loginStep === 'otp';
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  generateCaptcha(): void {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    this.captchaCode = Array.from(
      { length: 6 },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join('');
    this.captchaError = false;
  }

  selectTab(tab: 'industry' | 'official' | 'public'): void {
    this.activeTab = tab;
  }

  onLogin(): void {
    this.clearMessages();

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const captchaInput = this.loginForm.get('captchaInput')?.value;

    if (captchaInput !== this.captchaCode) {
      this.captchaError = true;
      this.refreshCaptchaInput(); // Generate a new CAPTCHA and clear the input
      this.loginError = 'Captcha does not match.';
      return;
    }
    this.captchaError = false;

    const username = String(this.loginForm.get('userId')?.value || '').trim();
    const password = String(this.loginForm.get('password')?.value || '');

    if (!username) {
      this.loginForm.get('userId')?.markAsTouched();
      return;
    }

    if (!password) {
      this.loginForm.get('password')?.markAsTouched();
      return;
    }

    this.isLoading = true;

    this.authService.login(username, password).subscribe({
      next: (res) => {
        this.isLoading = false;
        const normalized = this.normalizeAuthResponse(res);

        if (!normalized.status) {
          this.loginError = normalized.message || 'Invalid username or password. Please try again.';
          this.refreshCaptchaInput();
          return;
        }

        if (normalized.token && normalized.user) {
          this.completeLogin({
            status: true,
            message: normalized.message,
            token: normalized.token,
            user: normalized.user,
          });
          return;
        }

        this.openOtpStep(username, normalized.message);
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Login error:', err);
        this.loginError =
          this.parseApiMessage(err?.error?.message) ||
          'Invalid username or password. Please check your credentials and try again.';
        this.refreshCaptchaInput();
      },
    });
  }

  private openOtpStep(username: string, message: string): void {
    this.pendingUsername = username;
    this.loginStep = 'otp';
    this.loginSuccess =
      message || 'OTP sent to the registered mobile number linked with your account.';
    this.lockCredentialFields();
    this.resetOtpForm();
    this.startResendCountdown();

    setTimeout(() => {
      const firstOtpInput = document.getElementById('otp1') as HTMLInputElement | null;
      firstOtpInput?.focus();
    });
  }

  private lockCredentialFields(): void {
    ['userId', 'password', 'captchaInput', 'rememberMe'].forEach((name) => {
      this.loginForm.get(name)?.disable({ emitEvent: false });
    });
  }

  private unlockCredentialFields(): void {
    ['userId', 'password', 'captchaInput', 'rememberMe'].forEach((name) => {
      this.loginForm.get(name)?.enable({ emitEvent: false });
    });
  }

  private normalizeAuthResponse(res: unknown): NormalizedAuthResponse {
    const body = (res ?? {}) as Record<string, unknown>;
    const data = body['data'];
    const dataObj =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : null;

    const statusValue = body['status'];
    const status = statusValue === true || statusValue === 'true' || statusValue === 1;

    return {
      status,
      message: this.parseApiMessage(body['message'] ?? dataObj?.['message']),
      token: (body['token'] ?? dataObj?.['token']) as string | undefined,
      user: (body['user'] ?? dataObj?.['user']) as LoginUser | undefined,
    };
  }

  private parseApiMessage(message: unknown): string {
    if (Array.isArray(message)) {
      return message
        .map((item) => (typeof item === 'string' ? item : String(item)))
        .filter(Boolean)
        .join(' ');
    }

    if (typeof message === 'string') {
      return message;
    }

    return '';
  }

  onVerifyOtp(): void {
    this.clearMessages();
    this.otpForm.markAllAsTouched();

    if (!this.isOtpComplete) {
      this.loginError = 'OTP is required. Please enter the complete 4-digit code.';
      return;
    }

    this.isLoading = true;

    this.authService.verifyLoginOtp(this.pendingUsername, this.getOtpValue()).subscribe({
      next: (res) => {
        this.isLoading = false;
        const normalized = this.normalizeAuthResponse(res);

        if (normalized.status && normalized.token && normalized.user) {
          this.completeLogin({
            status: true,
            message: normalized.message,
            token: normalized.token,
            user: normalized.user,
          });
        } else {
          this.loginError = normalized.message || 'OTP verification failed. Please try again.';
          this.resetOtpForm();
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Verify OTP error:', err);
        this.loginError =
          this.parseApiMessage(err?.error?.message) ||
          'Invalid OTP. Please check the code and try again.';
        this.resetOtpForm();
      },
    });
  }

  onRegister() {
    this.showRegTypeModal = true;
  }

  selectRegistrationType(type: 'MANUFACTURING' | 'NON_MANUFACTURING' | 'TRADE') {
    this.showRegTypeModal = false;
    if (type === 'MANUFACTURING') {
      this.router.navigate(['/register']);
    } else if (type === 'NON_MANUFACTURING') {
      this.router.navigate(['/non-mnfg-register']);
    } else if (type === 'TRADE') {
      this.router.navigate(['/trade-register']);
    }
  }

  closeRegTypeModal(): void {
    this.showRegTypeModal = false;
  }

  onResendOtp(): void {
    if (this.resendCountdown > 0 || this.isResendLoading || !this.pendingUsername) {
      return;
    }

    this.clearMessages();
    this.isResendLoading = true;

    this.authService.resendLoginOtp(this.pendingUsername).subscribe({
      next: (res) => {
        this.isResendLoading = false;
        const normalized = this.normalizeAuthResponse(res);

        if (normalized.status) {
          this.loginSuccess =
            normalized.message || 'OTP has been resent to your registered mobile number.';
          this.resetOtpForm();
          this.startResendCountdown();
        } else {
          this.loginError = normalized.message || 'Unable to resend OTP. Please try again.';
        }
      },
      error: (err) => {
        this.isResendLoading = false;
        console.error('Resend OTP error:', err);
        this.loginError =
          this.parseApiMessage(err?.error?.message) ||
          'Unable to resend OTP. Please try again later.';
      },
    });
  }

  backToLogin(): void {
    this.loginStep = 'credentials';
    this.pendingUsername = '';
    this.clearMessages();
    this.unlockCredentialFields();
    this.resetOtpForm();
    this.clearResendTimer();
    this.refreshCaptchaInput();
  }

  onOtpInput(event: Event, currentField: string, nextField: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    input.value = value;
    this.otpForm.get(currentField)?.setValue(value);

    if (value && nextField) {
      const nextInput = document.getElementById(nextField) as HTMLInputElement | null;
      nextInput?.focus();
    }

    setTimeout(() => {
      if (this.isOtpComplete) {
        this.onVerifyOtp();
      }
    });
  }

  onOtpKeydown(event: KeyboardEvent, currentField: string, prevField: string): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !input.value && prevField) {
      const prevInput = document.getElementById(prevField) as HTMLInputElement | null;
      prevInput?.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, this.otpFields.length);

    this.otpFields.forEach((field, index) => {
      this.otpForm.get(field)?.setValue(digits[index] || '');
    });

    const focusIndex = Math.min(digits.length, this.otpFields.length - 1);
    const focusField = this.otpFields[focusIndex];
    const focusInput = document.getElementById(focusField) as HTMLInputElement | null;
    focusInput?.focus();

    setTimeout(() => {
      if (this.isOtpComplete) {
        this.onVerifyOtp();
      }
    });
  }

  get isOtpComplete(): boolean {
    return this.otpFields.every((field) => this.otpForm.get(field)?.valid);
  }

  getOtpValue(): string {
    return this.otpFields.map((field) => this.otpForm.get(field)?.value || '').join('');
  }

  //otp verifivcation and login completion delay to ensure that the user sees the success message before redirecting
  private completeLogin(res: LoginResponse): void {
    this.authService.storeSession(res);

    const dashboardRoute = this.authService.resolveDashboardRoute(res.user);

    this.loginSuccess = 'OTP verified successfully. Redirecting...';

    setTimeout(() => {
      this.router.navigate([dashboardRoute]);
    }, 3000);
  }

  private resetOtpForm(): void {
    this.otpForm.reset();
    for (const field of this.otpFields) {
      this.otpForm.get(field)?.setValue('');
    }
    setTimeout(() => {
      if (this.otpFields.length > 0) {
        document.getElementById(this.otpFields[0])?.focus();
      }
    });
  }

  private refreshCaptchaInput(): void {
    this.generateCaptcha();
    this.loginForm.get('captchaInput')?.reset();
  }

  private clearMessages(): void {
    this.loginError = '';
    this.loginSuccess = '';
  }

  private startResendCountdown(): void {
    this.clearResendTimer();
    this.resendCountdown = this.RESEND_COOLDOWN_SECONDS;

    this.resendTimerId = setInterval(() => {
      this.resendCountdown -= 1;
      if (this.resendCountdown <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
    this.resendCountdown = 0;
  }
}
